/**
 * Biometric auth wrapper — Face ID / Touch ID / fingerprint.
 *
 * Native: uses `@capgo/capacitor-native-biometric` (forked from
 *   `capacitor-native-biometric`, the only well-maintained Cap-7
 *   compatible plugin as of Feb 2026). Replaced
 *   `@aparajita/capacitor-biometric-auth` which hung indefinitely on
 *   iOS native builds (see Issue #194 — `authenticate()` promise
 *   never resolves when the pod isn't synced).
 *
 * Web (preview / browser): degrades gracefully — feature reports as
 * unsupported and the toggle won't render. The stored-credentials APIs
 * still work via localStorage for parity, but verification is a no-op
 * that resolves false on web so we never auto-sign-in without biometrics.
 *
 * Storage model:
 *   - kinly.biom.enabled          → "1" if the user has opted in.
 *   - kinly.biom.email            → the account email (used to pre-fill).
 *   - kinly.biom.password         → the account password.
 *     (stored only after a successful biometric verify; cleared on logout
 *      or when the user disables the toggle. On iOS native, future work
 *      should move this to Keychain via the plugin's setCredentials API.)
 */
import { Capacitor } from "@capacitor/core";

const KEY_ENABLED = "kinly.biom.enabled";
const KEY_EMAIL = "kinly.biom.email";
const KEY_PASSWORD = "kinly.biom.password";

let _plugin = null;
let _BiometryType = null;
async function getPlugin() {
  if (_plugin) return _plugin;
  try {
    const mod = await import("@capgo/capacitor-native-biometric");
    _plugin = mod.NativeBiometric || mod.default || null;
    _BiometryType = mod.BiometryType || null;
  } catch {
    _plugin = null;
  }
  return _plugin;
}

function isNative() {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

// Map plugin BiometryType enum (numeric) to our string labels.
function _mapBiometryType(t) {
  // @capgo plugin uses numeric enum:
  //  0 NONE, 1 TOUCH_ID, 2 FACE_ID, 3 FINGERPRINT, 4 FACE_AUTHENTICATION, 5 IRIS_AUTHENTICATION
  switch (t) {
    case 1: return "touchId";
    case 2: return "faceId";
    case 3: return "fingerprint";
    case 4: return "faceAuthentication";
    case 5: return "iris";
    default: return "none";
  }
}

/**
 * Returns { available, biometryType } where biometryType is one of:
 *  "faceId" | "touchId" | "fingerprint" | "faceAuthentication" | "iris" | "none"
 *
 * IMPORTANT: on iOS Capacitor, `isAvailable()` can return
 * `isAvailable: false` until the user has granted Face ID permission AT
 * LEAST ONCE in the app's lifetime — Apple gates the capability behind
 * the first authentication prompt. To avoid a chicken-and-egg lockout
 * ("the toggle is disabled, so I can never enable biometrics"), we treat
 * every native platform as POTENTIALLY available. The actual auth prompt
 * is the real gate — if the device truly has no biometrics enrolled, it
 * fails fast with a clear reason that we surface to the user.
 */
export async function checkBiometricAvailability() {
  if (!isNative()) return { available: false, biometryType: "none", reason: "web" };
  const plugin = await getPlugin();
  if (!plugin) {
    // Plugin is registered in package.json but not in the current native
    // bundle (older TestFlight build). Still treat as available — let
    // the user try; the bundled JS will surface an honest error if the
    // call fails.
    return { available: true, biometryType: "faceId", reason: "plugin_not_synced" };
  }
  try {
    const info = await plugin.isAvailable();
    return {
      available: !!info?.isAvailable,
      biometryType: _mapBiometryType(info?.biometryType),
      reason: info?.errorCode ? String(info.errorCode) : "",
    };
  } catch {
    // Optimistically treat every native platform as biometric-capable —
    // the real gate is the verifyIdentity() call, not the API report.
    return { available: true, biometryType: "faceId", reason: "check_threw" };
  }
}

/** Human label for a biometry type. */
export function biometryLabel(t) {
  switch (t) {
    case "faceId":
    case "faceAuthentication":
      return "Face ID";
    case "touchId":
      return "Touch ID";
    case "fingerprint":
      return "Fingerprint";
    case "iris":
      return "Iris";
    default:
      return "Biometric";
  }
}

/**
 * Triggers a biometric prompt. Resolves an `{ok, reason}` object on every
 * outcome so callers can show a meaningful message — instead of the old
 * boolean which silently swallowed real errors.
 *  - ok=true             → user authenticated successfully
 *  - ok=false, reason=…  → enriched reason: 'userCancel', 'notEnrolled',
 *                          'systemCancel', 'notAvailable', 'timeout',
 *                          'plugin_not_synced', 'web', 'unknown'
 *
 * The native plugin's promise is RACED against a 30s timeout — without
 * this, an iOS build that bundles the JS package but not the native pod
 * leaves verifyIdentity() pending forever, and the calling button is
 * stuck on its spinner with no way out. With the race, we always
 * return within 30s so the UI is never permanently locked.
 */
export async function verifyBiometric({ reason = "Sign in to Kinly", timeoutMs = 30000 } = {}) {
  if (!isNative()) return { ok: false, reason: "web" };
  const plugin = await getPlugin();
  if (!plugin) return { ok: false, reason: "plugin_not_synced" };
  try {
    const auth = plugin.verifyIdentity({
      reason,
      title: "Sign in to Kinly",
      subtitle: reason,
      description: "",
      useFallback: true,
      negativeButtonText: "Cancel",
    });
    let timer = null;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve({ __timeout: true }), timeoutMs);
    });
    const result = await Promise.race([
      auth.then(() => ({ __ok: true })).catch((e) => ({ __err: e })),
      timeout,
    ]);
    if (timer) clearTimeout(timer);
    if (result?.__timeout) {
      // @capgo plugin has no cancel() — but the prompt auto-dismisses on
      // the next user-initiated activity, so we surface the timeout to
      // the UI and let the user retry / fall back to password.
      return { ok: false, reason: "timeout", message: "Biometric prompt timed out. Try again or use your password." };
    }
    if (result?.__err) {
      const err = result.__err;
      // Plugin throws with `code` (string) on iOS and Android.
      const code = (err && (err.code || err.errorCode)) || "unknown";
      const msg = (err && err.message) || "Biometric authentication failed.";
      return { ok: false, reason: String(code), message: msg };
    }
    return { ok: true, reason: "" };
  } catch (err) {
    const code = (err && (err.code || err.errorCode)) || "unknown";
    const msg = (err && err.message) || "Biometric authentication failed.";
    return { ok: false, reason: String(code), message: msg };
  }
}

// ───── Credential storage (gated behind biometric verify) ─────

export function isBiometricEnabled() {
  try { return localStorage.getItem(KEY_ENABLED) === "1"; } catch { return false; }
}

export function getStoredCredentials() {
  try {
    return {
      email: localStorage.getItem(KEY_EMAIL) || "",
      password: localStorage.getItem(KEY_PASSWORD) || "",
    };
  } catch {
    return { email: "", password: "" };
  }
}

export function storeCredentials({ email, password }) {
  try {
    localStorage.setItem(KEY_ENABLED, "1");
    localStorage.setItem(KEY_EMAIL, email || "");
    localStorage.setItem(KEY_PASSWORD, password || "");
  } catch { /* noop */ }
}

export function clearBiometricCredentials() {
  try {
    localStorage.removeItem(KEY_ENABLED);
    localStorage.removeItem(KEY_EMAIL);
    localStorage.removeItem(KEY_PASSWORD);
  } catch { /* noop */ }
}

// ───── "Remember me" (just email persistence — no password) ─────

const KEY_REMEMBER = "kinly.remember.email";

export function getRememberedEmail() {
  try { return localStorage.getItem(KEY_REMEMBER) || ""; } catch { return ""; }
}

export function setRememberedEmail(email) {
  try {
    if (email) localStorage.setItem(KEY_REMEMBER, email);
    else localStorage.removeItem(KEY_REMEMBER);
  } catch { /* noop */ }
}
